import * as React from 'react';
import { useParams } from 'react-router-dom';
import { useTheme, useMediaQuery, Grid, Skeleton } from '@mui/material';
import { useDealQuery } from '../../shared/hooks';
import { CerebroText } from '../../shared/components';
import { addYears, format } from 'date-fns';
import { Deal } from '../../shared/models';
import { FinancialCoulmnsProps, getBlankColumnWidth } from './IncomeStatementRow';

interface IncomeStatementHeadingsProps {
	isLoading: boolean;
	deal: Deal | null | undefined;
	columns: FinancialCoulmnsProps;
}

export const IncomeStatementHeadings: React.FC<IncomeStatementHeadingsProps> = ({ isLoading, deal, columns }) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	if (isMobile) return null;

	const dealCreatedOn = deal?.financialStartDate;
	const blankColumnWidth = getBlankColumnWidth(columns);

	return (
		<Grid container spacing={2} sx={{ marginY: 1 }}>
			<Grid item md={2}></Grid>
			{columns.twoYearsAnyData && (
				<Grid item md={2}>
					<CerebroText align="right" weight="bold" variant="small">
						{isLoading && <Skeleton height={50} />}
						{!isLoading && getYearLabel('twoYearsAgo', dealCreatedOn)}
					</CerebroText>
				</Grid>
			)}
			{columns.previousYearAnyData && (
				<Grid item md={2}>
					<CerebroText align="right" weight="bold" variant="small">
						{isLoading && <Skeleton height={50} />}
						{!isLoading && getYearLabel('previousFullYear', dealCreatedOn)}
					</CerebroText>
				</Grid>
			)}
			<Grid item md={2}>
				<CerebroText align="right" weight="bold" variant="small">
					{isLoading && <Skeleton height={50} />}
					{!isLoading && getYearLabel('trailingTwelveMonths', dealCreatedOn)}
				</CerebroText>
			</Grid>
			{columns.projectedFullYearAnyData && (
				<Grid item md={2}>
					<CerebroText align="right" weight="bold" variant="small">
						{isLoading && <Skeleton height={50} />}
						{!isLoading && getYearLabel('projectedFullYear', dealCreatedOn)}
					</CerebroText>
				</Grid>
			)}
			{columns.futureYearAnyData && (
				<Grid item md={2}>
					<CerebroText align="right" weight="bold" variant="small">
						{isLoading && <Skeleton height={50} />}
						{!isLoading && getYearLabel('futureYear', dealCreatedOn)}
					</CerebroText>
				</Grid>
			)}
			{blankColumnWidth > 0 && (
				<Grid item md={blankColumnWidth}>
					&nbsp;
				</Grid>
			)}
		</Grid>
	);
};

export function getYearLabel(year: MetricYears, dealCreationDate?: string) {
	if (dealCreationDate === undefined) return;

	const effectiveDate = new Date(dealCreationDate);
	const dateYear = effectiveDate.getUTCFullYear();

	switch (year) {
		case 'twoYearsAgo':
			return 'Prior Year' + ` ${dateYear - 1}`;

		case 'previousFullYear':
			return `${dateYear} YTD`;

		case 'trailingTwelveMonths':
			return 'TTM (Trailing 12 Months)';

		case 'projectedFullYear':
			return `${dateYear} Projected Full Year`;

		case 'futureYear':
			return `${dateYear + 1} Projected Full Year`;

		default:
			return 'N/A';
	}
}

export type MetricYears =
	| 'twoYearsAgo'
	| 'previousFullYear'
	| 'trailingTwelveMonths'
	| 'projectedFullYear'
	| 'futureYear';
